import { IContextAndConfiguration } from '@kurtosys/types/context/index.js';
import { IQueryClassDependency } from '@kurtosys/types/dependencies/IQueryClassDependency';
import {
	IExecutionOptions,
	IQueryContext,
	IQueryContextConfiguration,
	IQueryKeyValue,
	IQueryOptions,
	IQueryResponseMapping,
	IQueryStaticMethodOptions,
} from '@kurtosys/types/query/index.js';

import { commonUtils, constants, stores } from '../../common/index.js';

export const contextStore = (): stores.ContextStore => {
	if (!(window as any)[constants.CONTEXT_STORE_WINDOW_KEY]) {
		(window as any)[constants.CONTEXT_STORE_WINDOW_KEY] = new stores.ContextStore();
	}
	return (window as any)[constants.CONTEXT_STORE_WINDOW_KEY];
};

export const getContextAndConfiguration = (
	executionOptions: IExecutionOptions,
	queryOptions: IQueryOptions,
): IContextAndConfiguration | undefined => {
	if (queryOptions) {
		const { defaultContextParentKey } = executionOptions;
		const { contextParentKey = defaultContextParentKey, contextKey = 'default' } = queryOptions;
		const workingContextStore = executionOptions.contextStore || contextStore();
		if (workingContextStore) {
			const contextAndConfiguration = workingContextStore.get({ contextParentKey, contextKey });
			return contextAndConfiguration;
		}
	}
	return undefined;
};
export const getContext = (
	executionOptions: IExecutionOptions,
	queryOptions: IQueryOptions,
): IQueryContext | undefined => {
	if (executionOptions) {
		const { context } = executionOptions;
		if (context) {
			return context;
		}
	}
	if (queryOptions) {
		const contextAndConfiguration = getContextAndConfiguration(executionOptions, queryOptions);
		return contextAndConfiguration && contextAndConfiguration.context;
	}
	return undefined;
};
export const getContextConfiguration = (
	executionOptions: IExecutionOptions,
	queryOptions: IQueryOptions,
): IQueryContextConfiguration | undefined => {
	if (executionOptions) {
		const { contextConfiguration } = executionOptions;
		if (contextConfiguration) {
			return contextConfiguration;
		}
	}
	if (queryOptions) {
		const contextAndConfiguration = getContextAndConfiguration(executionOptions, queryOptions);
		return contextAndConfiguration && contextAndConfiguration.contextConfiguration;
	}
	return undefined;
};
export const convertQueryKeyValue = (
	dependencies: IQueryClassDependency,
	queryKeyValue: IQueryKeyValue,
	executionOptions: IExecutionOptions,
	staticOptions?: IQueryStaticMethodOptions,
	omitKeysWithUndefinedValues?: boolean,
): Record<string, any> => {
	const response: { [key: string]: any } = {};
	if (queryKeyValue) {
		Object.keys(queryKeyValue).forEach((key) => {
			const valueQueryOptions = queryKeyValue[key];
			if (valueQueryOptions) {
				const valueQuery = new dependencies.queryClass(
					valueQueryOptions,
					staticOptions && staticOptions.translationHelper,
				);
				const value = valueQuery.execute(executionOptions);
				if (!omitKeysWithUndefinedValues || value !== undefined) {
					response[key] = value;
				}
			}
		});
	}
	return response;
};
export const convertQueryMime = (
	dependencies: IQueryClassDependency,
	queryMime: any,
	executionOptions: IExecutionOptions,
	staticOptions?: IQueryStaticMethodOptions,
): Record<string, any> => {
	const response: object = {};
	if (typeof queryMime === 'object') {
		const { _queryResponseMappings } = queryMime;
		const keys = Object.keys(queryMime);
		if (_queryResponseMappings) {
			for (const queryResponseMapping of _queryResponseMappings as IQueryResponseMapping[]) {
				loadQueryResponseMapping(
					dependencies,
					queryResponseMapping,
					response,
					queryMime,
					executionOptions,
					keys,
					staticOptions,
				);
			}
		}
		keys.forEach((key) => {
			loadQueryMimeKey(dependencies, response, key, queryMime, executionOptions, staticOptions);
		});
		return response;
	}
	return queryMime;
};
export const loadQueryResponseMapping = (
	dependencies: IQueryClassDependency,
	queryResponseMapping: IQueryResponseMapping,
	response: object,
	queryMime: any,
	executionOptions: IExecutionOptions,
	keys: string[],
	staticOptions?: IQueryStaticMethodOptions,
): void => {
	const { key, children } = queryResponseMapping;
	loadQueryMimeKey(dependencies, response, key, queryMime, executionOptions, staticOptions);
	const keyIndex = keys.indexOf(key);
	if (keyIndex > -1) {
		keys.splice(keyIndex, 1);
	}
	const parentResponse = response[key];
	if (children && commonUtils.isArrayLike(children) && children.length > 0) {
		for (const child of children) {
			loadQueryResponseMapping(
				dependencies,
				child,
				response,
				queryMime,
				{ ...executionOptions, responseContext: parentResponse },
				keys,
				staticOptions,
			);
		}
	}
};
export const loadQueryMimeKey = (
	dependencies: IQueryClassDependency,
	response: object,
	key: string,
	queryMime: any,
	executionOptions: IExecutionOptions,
	staticOptions?: IQueryStaticMethodOptions,
): void => {
	const value = queryMime[key];
	if (value) {
		if (commonUtils && commonUtils.isArrayLike(value)) {
			response[key] = (value as any[]).map((item) => {
				// If this object is an IQueryOption, the query the value
				if (item.queryOptionsType) {
					return getQueryValue(dependencies, item, executionOptions, staticOptions);
				}
				return convertQueryMime(dependencies, item, executionOptions, staticOptions);
			});
		} else if (typeof value === 'object') {
			if (value.queryOptionsType) {
				response[key] = getQueryValue(dependencies, value, executionOptions, staticOptions);
			} else {
				response[key] = convertQueryMime(dependencies, value as any, executionOptions, staticOptions);
			}
		} else {
			response[key] = value;
		}
	}
};

const getQueryValue = (
	dependencies: IQueryClassDependency,
	value: any,
	executionOptions: IExecutionOptions,
	staticOptions?: IQueryStaticMethodOptions,
): any => {
	const valueQuery = new dependencies.queryClass(
		value as IQueryOptions,
		staticOptions && staticOptions.translationHelper,
	);
	const responseValue = valueQuery.execute(executionOptions);
	return responseValue;
};
